import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNode, useEditor, useEditingCond } from '@splashthat/splash-cms-engine';
import SessionCard from 'common/components/SessionCard';
import { SessionText } from 'common/components/SessionCard/SessionCard.styles';
import useSessionsRoutes from 'common/components/TouchpointEditor/hooks/useSessionsRoutes';
import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import { usePreview } from 'common/components/TouchpointEditor/hooks/usePreview';

import { useModal } from 'common/components/TouchpointEditor/contexts/ModalManager';
import { useAttendeeSessions } from 'common/components/TouchpointEditor/contexts/AttendeeSessionContext';
import { getRegisteredSessionsStatus } from 'common/components/TouchpointEditor/utils/index';
import { useSessionRegistration } from 'common/components/TouchpointEditor/hooks/useSessionRegistration';
import { EMBED_TYPE_ZOOM } from 'common/constants/virtualEvent';
import { STATUS_RSVP_YES, STATUS_CHECKIN_YES, STATUS_CHECKIN_NO } from 'common/schemas/sessionGuest';

import Spacer from 'common/components/Spacer';
import { useFlags } from 'utils/react-context';

import JoinSessionButton from './JoinSessionButton';
import WatchSessionButton from './WatchSessionButton';

import { descendantsDefaultProps, descendantsPropTypes } from './defaults';

import { AttendeeSessionsListBlockContentContainer } from './AttendeeSessionsList.styles';
import { NODE_ID_SPEAKER_MODALS, NODE_ID_UPCOMING_SESSION_CARDS } from '../../constants';

export default function AttendeeSessionsList({
    selected,
    hovered,
    descendants: {
        card,
        title,
        date,
        speakerName,
        speakerTitleCompany,
        description,
        tag,
        sessionButton,
        zoomEmbedMessage,
    },
    status,
}) {
    const node = useNode();
    const editor = useEditor((state) => ({
        editing: state.editing,
    }));
    const sessionsRoutes = useSessionsRoutes();
    const modal = useModal();
    const {
        theme: { colors, activeFonts },
        event,
    } = useTouchpointData();
    const attendeeSessions = useAttendeeSessions();
    const sessionRegistration = useSessionRegistration();
    const { shouldPreview } = usePreview({ event });
    const isJoinButtonEnabledForced = editor.editing || shouldPreview;
    const isUpcoming = status === 'upcoming';
    const { zoomSdkObfTokenEnabled } = useFlags();

    const onClickSpeakerModalIfEditing = () => editor.actions.selectNode(NODE_ID_UPCOMING_SESSION_CARDS);
    const onClickSpeakerModalIfNotEditing = useCallback(
        (speakerInfo) =>
            speakerInfo.bio &&
            modal.open(NODE_ID_SPEAKER_MODALS, {
                speaker: speakerInfo,
            }),
        [],
    );

    const sessions = isUpcoming ? attendeeSessions.sessions : attendeeSessions.pastSessions;

    const registeredSessions = editor.editing ? sessions : getRegisteredSessionsStatus(sessionRegistration, sessions);
    const openSpeakerModal = useEditingCond(onClickSpeakerModalIfEditing, onClickSpeakerModalIfNotEditing);

    const setContent = useCallback(
        (text) =>
            node.actions.setProp((editedNode) => {
                editedNode.descendants.sessionButton.meta.content = text; // eslint-disable-line no-param-reassign
                return editedNode;
            }),
        [],
    );

    const sharedSessionButtonProps = {
        colors,
        activeFonts,
        styles: sessionButton.styles,
        meta: sessionButton.meta,
        setContent,
        disableContentEditing: !editor.editing || (!selected && !hovered),
    };

    return (
        <AttendeeSessionsListBlockContentContainer
            selected={selected}
            hovered={hovered}
            onMouseLeave={node.actions.onMouseLeave()}
            onMouseMove={node.actions.onMouseMove()}
            onClick={node.actions.onClick()}
        >
            {registeredSessions.map((session) => {
                const registrationNotRequired = !session.registration_required;
                const isAttending = session.registrationStatus === STATUS_RSVP_YES;
                const isCheckInYes = session.registrationStatus === STATUS_CHECKIN_YES;
                const isCheckInNo = session.registrationStatus === STATUS_CHECKIN_NO;
                const shouldDisplay =
                    editor.editing || registrationNotRequired || isAttending || isCheckInYes || isCheckInNo;
                const isZoomEmbed = session?.virtualVenue?.embeds?.[0]?.type === EMBED_TYPE_ZOOM;
                const zoomRedirectionMessage = session?.virtualVenue?.embeds?.[0]?.options?.zoom?.embedMessage;
                const hasOnDemandRecording = !!session?.virtualVenue?.embeds?.[0]?.onDemandRecording;

                return (
                    shouldDisplay && (
                        <SessionCard
                            key={session.id}
                            session={session}
                            colors={colors}
                            activeFonts={activeFonts}
                            cardStyles={card.styles}
                            titleStyles={title.styles}
                            dateStyles={date.styles}
                            speakerNameStyles={speakerName.styles}
                            speakerTitleCompanyStyles={speakerTitleCompany.styles}
                            descriptionStyles={description.styles}
                            tagStyles={tag.styles}
                            onClickSpeakerAvatar={openSpeakerModal}
                            footerComponent={
                                <>
                                    {zoomSdkObfTokenEnabled &&
                                    isZoomEmbed &&
                                    zoomRedirectionMessage &&
                                    (isUpcoming || hasOnDemandRecording) ? (
                                        <>
                                            <Spacer $spacing="1.5rem" />
                                            <SessionText
                                                $fontSize={zoomEmbedMessage.styles.fontSize}
                                                $fontColor={colors[zoomEmbedMessage.styles.fontColor]}
                                                $fontFamily={activeFonts[zoomEmbedMessage.styles.fontFamily]}
                                                data-qa={'zoom-redirection-message'}
                                            >
                                                {zoomRedirectionMessage}
                                            </SessionText>
                                        </>
                                    ) : null}
                                    <Spacer $spacing="1.5rem" />
                                    {isUpcoming ? (
                                        <JoinSessionButton
                                            {...sharedSessionButtonProps}
                                            startTime={session.start}
                                            isJoinButtonEnabledForced={isJoinButtonEnabledForced}
                                            to={`${sessionsRoutes.joinSessionUrl}${session.id}`}
                                        />
                                    ) : (
                                        <WatchSessionButton
                                            {...sharedSessionButtonProps}
                                            isWatchButtonEnabled={hasOnDemandRecording}
                                            to={`${sessionsRoutes.joinSessionUrl}${session.id}`}
                                        />
                                    )}
                                </>
                            }
                        />
                    )
                );
            })}
        </AttendeeSessionsListBlockContentContainer>
    );
}

AttendeeSessionsList.splash = {
    related: {
        settings: React.lazy(() => import('./AttendeeSessionsList.tools')),
    },
    defaultProps: {
        styles: {},
        descendants: descendantsDefaultProps, // THIS IS AN EXCEPTION FOR THE VEP
    },
};

AttendeeSessionsList.propTypes = {
    selected: PropTypes.bool.isRequired,
    hovered: PropTypes.bool.isRequired,
    descendants: PropTypes.shape(descendantsPropTypes).isRequired,
    status: PropTypes.oneOf(['upcoming', 'past']),
};

AttendeeSessionsList.defaultProps = {
    className: '',
    status: 'upcoming',
};
